import { defineStore } from 'pinia'
import { localStorage, ExpirableStorage } from '../../utils/storage/index'

import { getConfigStorePaths } from '../../utils/getConfigStorePaths'

import debug from '../../utils/console'
const logger = debug.withTag('store:ConfigStore')

export interface ConfigStore<T> {
  data: Ref<T>
  isLoading: Ref<boolean>
  load: () => Promise<T>
  update: (data: Partial<T>) => void
  reset: () => void
}

export interface ConfigStoreOptions<T> {
  defaults: T
  configStoreKey: string
  schema: (data: unknown) => T
  onLoad?: (data: T) => void
  onUpdate?: (data: T) => void
  onReset?: () => void
  transform?: (data: T) => T
}

export function useConfigStore<T>(props: ConfigStoreOptions<T>) {
  const applySafeTransform = (data: T): T => {
    if (!props.transform) return data

    try {
      return props.transform(data)
    } catch (err) {
      logger.error(
        `[${props.configStoreKey}] Failed to apply transform function:`,
        err
      )
      return data
    }
  }

  return defineStore(props.configStoreKey, () => {
    const storageWrapper = new ExpirableStorage(
      localStorage,
      `app:config`,
      60 * 1000 // 1 minute
    )

    const data = ref<T>(props.defaults)
    const isLoading = ref(false)

    const fetchConfig = async () => {
      const errors: string[] = []
      const urls = await getConfigStorePaths(props.configStoreKey)
      for (const url of Array.isArray(urls) ? urls : [urls]) {
        try {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Failed to fetch ${url}: ${response.statusText}`)
          }
          const json = await response.json()
          const validated = props.schema(json)
          return validated
        } catch (error) {
          errors.push(`Failed to fetch ${url}: ${error}`)
        }
      }
      throw new Error(`Failed to fetch config: ${errors.join(', ')}`)
    }

    let loadPromise: Promise<T> | null = null
    const load = async (): Promise<T> => {
      if (loadPromise) {
        return loadPromise
      }

      isLoading.value = true

      const stored = storageWrapper.load(props.configStoreKey)

      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          const validated = props.schema(parsed)
          const transformed = applySafeTransform(validated)
          data.value = transformed
          props.onLoad?.(transformed)
        } catch (err) {
          logger.warn(
            `[${props.configStoreKey}] Failed to parse or validate stored data:`,
            err
          )
        }
      } else {
        try {
          const validated = await fetchConfig()
          const transformed = applySafeTransform(validated)
          data.value = transformed
          storageWrapper.save(props.configStoreKey, JSON.stringify(transformed))
          props.onLoad?.(transformed)
        } catch (err) {
          logger.error(
            `[${props.configStoreKey}] Failed to fetch or validate config:`,
            err
          )
        }
        if (!data.value) {
          logger.warn(
            `[${props.configStoreKey}] No valid data found in local storage or API`
          )
        }
      }
      isLoading.value = false
      return await Promise.resolve(data.value)
    }
    // Load the config when the store is created
    loadPromise = load()

    const update = (newData: Partial<T>) => {
      logger.log('updating config store', newData)
      const merged = { ...data.value, ...newData }
      data.value = applySafeTransform(merged)
      storageWrapper.save(props.configStoreKey, JSON.stringify(data.value))
      props.onUpdate?.(data.value)
    }

    const reset = () => {
      const clone = structuredClone(props.defaults)
      data.value = applySafeTransform(clone)
      storageWrapper.save(props.configStoreKey, JSON.stringify(data.value))
      props.onReset?.()
      loadPromise = null
    }

    return {
      data,
      isLoading,
      load,
      update,
      reset,
      storage: storageWrapper,
      initialized: loadPromise,
    }
  })
}
